import React, { useState, useEffect } from "react";
import {
  AiOutlineFolderAdd,
  AiOutlineFileAdd,
  AiOutlineFolder,
  AiOutlineFolderOpen,
  AiOutlineDelete,
  AiOutlineEdit
} from "react-icons/ai";
import {
  ActionsWrapper,
  Collapse,
  StyledFolder,
  StyledName,
  VerticalLine
} from "./Tree.style";
import { useTreeContext } from "./TreeContext";
import { PlaceholderInput } from "./TreePlaceholderInput";
import { Checkbox } from "@fluentui/react";
import { useDrop } from 'react-dnd';
import { NativeTypes } from "react-dnd-html5-backend";
import { FolderName } from "./FolderName";

const NonEditingContent = React.memo(({
  name,
  isOpen,
  isImparative,
  onToggleOpen,
  onRename,
  onCreateFolder,
  onDelete
}: {
  name: string;
  isOpen: boolean;
  isImparative: boolean;
  onToggleOpen: () => void;
  onRename: (event: React.MouseEvent) => void;
  onCreateFolder: (event: React.MouseEvent) => void;
  onDelete: () => void;
}) => {
  return (
    <div className="displayFlex">
      <FolderName
        name={name}
        isOpen={isOpen}
        handleClick={onToggleOpen}
      />
      {isImparative && (
        <div className="actions">
          <AiOutlineEdit onClick={onRename} />
          <AiOutlineFolderAdd onClick={onCreateFolder} />
          <AiOutlineDelete onClick={onDelete} />
        </div>
      )}
    </div>
  );
});

const EditingContent = React.memo(({
  name,
  level,
  onSubmit
}: {
  name: string;
  level: number;
  onSubmit: (name: string) => Promise<void>;
}) => {
  return (
    <div className="displayFlex">
      <PlaceholderInput
        type="folder"
        style={{ marginLeft: 0 }}
        folderLevel={level - 2}
        defaultValue={name}
        handleSubmit={onSubmit}
      />
    </div>
  );
});

interface FolderProps {
  id?: string;
  name: string;
  level?: number;
  children?: React.ReactNode;
  parentPath?: string;
  selected?: boolean;
  onSelect?: (checked: boolean) => void;
  onEdit?: () => void;
  onDelete?: (deleteCallback: () => void) => void;
  onCreateFolder?: (name: string, createCallback: () => void) => void;
  onCreateFile?: (name: string, createCallback: () => void) => void;
  onRename?: (name: string) => Promise<void>;
}

const Folder = React.memo(({ id, name, level = 1, children, parentPath = "", selected = false, onSelect = (checked: boolean) => { }, onEdit = () => { }, onDelete = () => { },
  onCreateFolder = () => { }, onCreateFile = () => { }, onRename = async (name: string) => { } }: FolderProps) => {
  const { state, dispatch, isImparative, onNodeClick } = useTreeContext();
  const [isEditing, setEditing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [childs, setChilds] = useState<React.ReactNode[]>([]);

  useEffect(() => {
    const nestedChilds = React.Children.map(children, item => {
      if (React.isValidElement(item) && item.type === Folder) {
        return React.cloneElement(item as React.ReactElement<FolderProps>, {
          level: level + 1,
          parentPath: `${parentPath}/${name}`
        });
      }
      return item;
    });
    setChilds(nestedChilds || []);
  }, [children, level, name, parentPath]);

  const handleFileCreation = React.useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setIsOpen(true);
    setChilds([
      ...childs,
      <PlaceholderInput
        type="file"
        handleSubmit={(name: string) => {
          onCreateFile(name, () => {
            dispatch({ type: "CREATE_FILE", payload: { id, name } });
          });
        }}
      />
    ]);
  }, [childs, onCreateFile, dispatch, id]);

  const handleFolderCreation = React.useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setIsOpen(true);
    setChilds([
      ...childs,
      <PlaceholderInput
        type="folder"
        folderLevel={level}
        handleSubmit={(name: string) => {
          onCreateFolder(name, () => {
            dispatch({ type: "CREATE_FOLDER", payload: { id, name } });
          });
        }}
      />
    ]);
  }, [childs, onCreateFolder, dispatch, id, level]);

  const handleDeleteFolder = React.useCallback(() => {
    onDelete(() => {
      dispatch({ type: "DELETE_FOLDER", payload: { id } });
    });
  }, [onDelete, dispatch, id]);

  const handleFolderRename = React.useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    console.log('handleFolderRename called, current isEditing:', isEditing);
    setEditing(true);
  }, [isEditing]);

  const handleSubmit = React.useCallback(async (newName: string) => {
    try {
      await onRename(newName);
      dispatch({ type: "RENAME_FOLDER", payload: { id, name: newName } });
    } finally {
      setEditing(false);
    }
  }, [onRename, dispatch, id]);

  interface DroppableProperties {
    accept?: any;
    children?: any;
    theme?: any;
    onDrop: (payload: any) => void
  }

  const Droppable = (properties: DroppableProperties) => {
    const [{ isOver }, drop] = useDrop(() => ({
      accept: [NativeTypes.FILE],
      drop: properties.onDrop,
      collect: (monitor: any) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop()
      })
    }));

    return <div
      ref={drop}
      style={{ backgroundColor: isOver ? 'rgba(255, 0, 0, 0.05)' : 'transparent' }}
    >
      {properties && properties.children
        ?
        (properties.children)
        :
        <></>
      }
    </div>;
  }

  return (
    <div className="displayFlex alignItemsTop">
      <Checkbox className="marginRightS" checked={selected} styles={{ root: { marginTop: '3px' } }} onChange={(_, checked) => onSelect(checked || false)} />
      <StyledFolder
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          onNodeClick({
            state,
            name,
            level,
            path: `${parentPath}/${name}`,
            type: "folder"
          });
        }}
        className="tree__folder"
      >
        <VerticalLine>
          <Droppable onDrop={(payload: any) => {
            console.log('drop', payload.files);
          }}>
            <ActionsWrapper>
              {isEditing ? (
                <EditingContent
                  name={name}
                  level={level}
                  onSubmit={handleSubmit}
                />
              ) : (
                <NonEditingContent
                  name={name}
                  isOpen={isOpen}
                  isImparative={isImparative}
                  onToggleOpen={() => setIsOpen(!isOpen)}
                  onRename={handleFolderRename}
                  onCreateFolder={handleFolderCreation}
                  onDelete={handleDeleteFolder}
                />
              )}
            </ActionsWrapper>
          </Droppable>
          <Collapse className="tree__folder--collapsible" isOpen={isOpen}>
            {childs}
          </Collapse>
        </VerticalLine>
      </StyledFolder>
    </div>
  );
});

export { Folder };
